import { NumberType } from './types'
import {
    Step2ValueModel,
    Step3ValueModel,
    Step1ValueModel,
    Step4ValueModel,
    Step5ValueModel,
} from './types'
import {
    validateOrganisationNumber,
    OrganisationType,
    validateCompanyName,
    validateZipCode,
    validateCity,
    validateToC,
} from '../shared/Validation'
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import {
    appendError,
    removeAllErrorsForKey,
    removeError,
    replaceError, validateIsEmail, validateIsNumber,
    validateRequired
} from "@sharedComponents/PantaForm/utils";
import i18n from "@apps/Shared/translation/i18n";
import {
    IValidateVatNumberResponse,
    VatNrValidationStatus
} from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import CompanyRegistrationsService from "@apps/Shared/Services/CompanyRegistrations/CompanyRegistrations.service";
import { StatusCode } from "@apps/Shared/Services/Response.types";
import { InputSelectOption } from "@sharedComponents/InputSelect/types";
import { ArticleSupplierType } from "@apps/Shared/Services/CompanyInformations/interfaces";

export enum ContactTypes {
    Agreement = 'agreement',
    Signatory = 'signatory',
    ProductManager = 'productManager',
    SalesReporting = 'salesReporting',
    Combined = 'combined',
}

export const validateOrgNrOrVatNr = (
    organizationNumber: string | undefined,
    vatNumber: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    if (organizationNumber && vatNumber) {
        errors = appendError(
            currentErrors,
            'organizationNumber',
            i18n.t('error.emptyOrgNrVatnr')
        )
    } else {
        errors = removeError(
            currentErrors,
            'organizationNumber',
            i18n.t('error.emptyOrgNrVatnr')
        )
    }

    errors = validateRequired(
        `${organizationNumber}${vatNumber}`,
        'organizationNumber',
        i18n.t('error.emptyOrgNrVatnr'),
        currentErrors
    )

    return errors
}

export const validateVatNumber = async (
    vatNumber: string | undefined,
    currentErrors: FormErrorType[],
    apiValidate: boolean,
    required: boolean
) => {
    let errors = required
        ? validateRequired(
              vatNumber,
              'vatNumber',
              i18n.t('error.emptyVatnr'),
              currentErrors
          )
        : ([] as FormErrorType[])

    if (vatNumber && apiValidate) {
        const [validationResult, unexpectedError]: [
            IValidateVatNumberResponse | undefined,
            string
        ] = await CompanyRegistrationsService.ValidateVatNr(vatNumber).then(
            res => {
                if (res.response?.status === StatusCode.OK) {
                    return [res.response.data, '']
                }

                if (res.errors && res.errors.length > 0) {
                    return [undefined, res.errors[0]]
                }

                return [undefined, '']
            }
        )

        if (unexpectedError) {
            errors = replaceError(errors, 'vatNumber', unexpectedError)
        } else if (!validationResult) {
            errors = replaceError(
                errors,
                'vatNumber',
                i18n.t('error.invalidVatnr')
            )
        } else if (
            validationResult.validation === VatNrValidationStatus.Exists
        ) {
            errors = replaceError(
                errors,
                'vatNumber',
                i18n.t('error.alreadyExistsVatnr')
            )
        } else if (
            validationResult.validation === VatNrValidationStatus.Invalid
        ) {
            errors = replaceError(
                errors,
                'vatNumber',
                i18n.t('error.invalidVatnr')
            )
        } else if (
            validationResult.validation === VatNrValidationStatus.OnGoing
        ) {
            errors = replaceError(
                errors,
                'vatNumber',
                i18n.t('error.onGoingRegistrationVATNumber')
            )
        } else {
            errors = removeAllErrorsForKey(errors, 'vatNumber')
        }
    }

    return errors
}

export const validateStreetAddress = (
    streetAddress: string | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateRequired(
        streetAddress,
        'streetAddress',
        i18n.t('error.emptyStreetAddress'),
        currentErrors
    )
    return errors
}

export const validateCountry = (
    country: InputSelectOption | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateRequired(
        country?.value,
        'country',
        i18n.t('error.emptyCountry'),
        currentErrors
    )
    return errors
}

export const validateEmail = (
    email: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = validateRequired(
        email,
        'email',
        i18n.t('error.emptyEmail'),
        currentErrors
    )

    if (email) {
        errors = validateIsEmail(
            email,
            'email',
            i18n.t('error.incorrectEmail'),
            currentErrors
        )
    }
    return errors
}

export const validateMetalPackaging = (
    number: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    if (!number) {
        errors = appendError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    } else {
        errors = removeError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    }

    return errors
}

export const validateMetalPackagingFormat = (
    number: string | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateIsNumber(
        number,
        'metalPackaging',
        i18n.t('error.invalidMetalPackaging'),
        currentErrors
    )

    return errors
}

export const validatePlasticPackagingLarge = (
    number: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]
    if (!number) {
        errors = appendError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    } else {
        errors = removeError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    }

    return errors
}

export const validatePlasticPackagingLargeFormat = (
    number: string | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateIsNumber(
        number,
        'plasticPackagingLarge',
        i18n.t('error.invalidPlasticPackagingLarge'),
        currentErrors
    )

    return errors
}

export const validatePlasticPackagingSmall = (
    number: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    if (!number) {
        errors = appendError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    } else {
        errors = removeError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    }

    return errors
}

export const validatePlasticPackagingSmallFormat = (
    number: string | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateIsNumber(
        number,
        'plasticPackagingSmall',
        i18n.t('error.invalidPlasticPackagingSmall'),
        currentErrors
    )

    return errors
}

const formatContactValidationText = (
    messageKey: string,
    contactType?: ContactTypes
) => {
    let sectionErrors = ''

    if (contactType) {
        sectionErrors = i18n.t(
            `companyRegistration.sectionErrors.${contactType}`
        )
    }
    return i18n.t(messageKey, {
        sectionText: sectionErrors,
    })
}

export const validateFirstname = (
    firstname: string | undefined,
    currentErrors: FormErrorType[],
    contactType: ContactTypes
) => {
    const errors = validateRequired(
        firstname,
        `${contactType}Firstname`,
        formatContactValidationText(
            'error.emptyRegistraionFirstname',
            contactType
        ),
        currentErrors
    )
    return errors
}

export const validateLastname = (
    lastname: string | undefined,
    currentErrors: FormErrorType[],
    contactType: ContactTypes
) => {
    const errors = validateRequired(
        lastname,
        `${contactType}Lastname`,
        formatContactValidationText(
            'error.emptyRegistraionLastname',
            contactType
        ),
        currentErrors
    )
    return errors
}

export const validateContactEmail = (
    email: string | undefined,
    currentErrors: FormErrorType[],
    contactType?: ContactTypes
) => {
    let errors = []

    errors = validateRequired(
        email,
        `${contactType ? contactType + 'Email' : 'email'}`,
        formatContactValidationText('error.emptyRegistraionEmail', contactType),
        currentErrors
    )

    if (email) {
        errors = validateIsEmail(
            email || '',
            `${contactType ? contactType + 'Email' : 'email'}`,
            formatContactValidationText(
                'error.incorrectRegistraionEmail',
                contactType
            ),
            currentErrors
        )
    }

    return errors
}

export const validateMobileNumber = (
    mobileNumber: string | undefined,
    currentErrors: FormErrorType[],
    contactType: ContactTypes
) => {
    const errors = validateRequired(
        mobileNumber,
        `${contactType}MobileNumber`,
        formatContactValidationText(
            'error.emptyRegistraionMobileNumber',
            contactType
        ),
        currentErrors
    )
    return errors
}

export const validateSignatoryAfterCopy = async (
    stepValues: any,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    errors = validateFirstname(
        stepValues.firstname,
        errors,
        ContactTypes.Signatory
    )

    errors = validateLastname(
        stepValues.lastname,
        errors,
        ContactTypes.Signatory
    )

    errors = validateContactEmail(
        stepValues.email,
        errors,
        ContactTypes.Signatory
    )

    errors = validateMobileNumber(
        stepValues.mobileNumber,
        errors,
        ContactTypes.Signatory
    )

    return errors
}

export const validateStep1 = async (
    stepValues: Step1ValueModel,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]
    if (stepValues.organizationNumber && stepValues.numberType === 'org') {
        const validationResult = await validateOrganisationNumber(
            OrganisationType.Supplier,
            stepValues.organizationNumber,
            errors,
            true,
            false
        )
        errors = validationResult?.errors
    } else if (stepValues.vatNumber && stepValues.numberType === 'vat') {
        errors = await validateVatNumber(
            stepValues.vatNumber,
            errors,
            true,
            false
        )
    } else {
        errors = validateOrgNrOrVatNr(
            stepValues.organizationNumber,
            stepValues.vatNumber,
            errors
        )
    }
    return errors
}

export const validateSalesReportingAfterCopy = async (
    stepValues: any,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    errors = validateFirstname(
        stepValues.firstname,
        errors,
        ContactTypes.SalesReporting
    )

    errors = validateLastname(
        stepValues.lastname,
        errors,
        ContactTypes.SalesReporting
    )

    errors = validateContactEmail(
        stepValues.email,
        errors,
        ContactTypes.SalesReporting
    )

    errors = validateMobileNumber(
        stepValues.mobileNumber,
        errors,
        ContactTypes.SalesReporting
    )

    return errors
}

export const validateStep2 = async (
    stepValues: Step2ValueModel,
    numberType: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]
    const validateZipCodeFormat =
        stepValues.numberType === NumberType.OrganisationNumber
    if (stepValues.numberType === 'org') {
        const validationResult = await validateOrganisationNumber(
            OrganisationType.Supplier,
            stepValues.organizationNumber,
            errors,
            false,
            true
        )
        errors = validationResult?.errors
    } else if (stepValues.numberType === 'vat') {
        errors = await validateVatNumber(
            stepValues.vatNumber,
            errors,
            false,
            true
        )
    }
    errors = validateSupplierType(stepValues.selectedSupplierType, errors)
    errors = validateCompanyName(stepValues.companyName, errors)
    errors = validateStreetAddress(stepValues.streetAddress, errors)
    errors = validateZipCode(stepValues.zipCode, validateZipCodeFormat, errors)
    errors = validateCity(stepValues.city, errors)
    if (stepValues.numberType === 'vat') {
        errors = validateCountry(stepValues.country, errors)
    }
    errors = validateContactEmail(stepValues.email, errors)

    return errors
}

export const validateSupplierType = (
    supplierTypes: ArticleSupplierType[],
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]
    //Kolla så att valideringen kikar på att saker är true
    if (!supplierTypes || supplierTypes.length <= 0) {
        errors = appendError(
            errors,
            'supplierType',
            i18n.t('error.emptySupplierType')
        )
    } else {
        let isTypeFound = false
        supplierTypes.forEach(item => {
            if (item.isType === true) {
                isTypeFound = true
            }
        })
        if (isTypeFound) {
            errors = removeError(
                errors,
                'supplierType',
                i18n.t('error.emptySupplierType')
            )
        } else {
            errors = appendError(
                errors,
                'supplierType',
                i18n.t('error.emptySupplierType')
            )
        }
    }
    return errors
}

export const validateStep3 = async (
    stepValues: Step3ValueModel,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]
    let hasField = false

    if (stepValues.metalPackaging) {
        errors = validateMetalPackagingFormat(stepValues.metalPackaging, errors)
        hasField = true
    }

    if (stepValues.plasticPackagingSmall) {
        errors = validatePlasticPackagingSmallFormat(
            stepValues.plasticPackagingSmall,
            errors
        )
        hasField = true
    }

    if (stepValues.plasticPackagingLarge) {
        errors = validatePlasticPackagingLargeFormat(
            stepValues.plasticPackagingLarge,
            errors
        )
        hasField = true
    }

    if (!hasField) {
        errors = appendError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    } else {
        errors = removeError(
            errors,
            'fieldsCheck',
            i18n.t('error.emptyProjectedSales')
        )
    }

    return errors
}

export const validateStep4 = async (
    stepValues: Step4ValueModel,
    currentErrors: FormErrorType[],
    combined: boolean | undefined
) => {
    let errors = [...currentErrors]

    if (combined) {
        const fields = stepValues.contactInfo?.combined
        errors = validateFirstname(
            fields?.firstname,
            errors,
            ContactTypes.Combined
        )
        errors = validateLastname(
            fields?.lastname,
            errors,
            ContactTypes.Combined
        )
        errors = validateContactEmail(
            fields?.email,
            errors,
            ContactTypes.Combined
        )
        errors = validateMobileNumber(
            fields?.mobileNumber,
            errors,
            ContactTypes.Combined
        )
        return errors
    }

    const agreement = stepValues.contactInfo?.agreement
    errors = validateFirstname(
        agreement?.firstname,
        errors,
        ContactTypes.Agreement
    )
    errors = validateLastname(
        agreement?.lastname,
        errors,
        ContactTypes.Agreement
    )
    errors = validateContactEmail(
        agreement?.email,
        errors,
        ContactTypes.Agreement
    )
    errors = validateMobileNumber(
        agreement?.mobileNumber,
        errors,
        ContactTypes.Agreement
    )

    const signatory = stepValues.contactInfo?.signatory
    errors = validateFirstname(
        signatory?.firstname,
        errors,
        ContactTypes.Signatory
    )
    errors = validateLastname(
        signatory?.lastname,
        errors,
        ContactTypes.Signatory
    )
    errors = validateContactEmail(
        signatory?.email,
        errors,
        ContactTypes.Signatory
    )
    errors = validateMobileNumber(
        signatory?.mobileNumber,
        errors,
        ContactTypes.Signatory
    )

    const prodmngr = stepValues.contactInfo?.productManager
    errors = validateFirstname(
        prodmngr?.firstname,
        errors,
        ContactTypes.ProductManager
    )
    errors = validateLastname(
        prodmngr?.lastname,
        errors,
        ContactTypes.ProductManager
    )
    errors = validateContactEmail(
        prodmngr?.email,
        errors,
        ContactTypes.ProductManager
    )
    errors = validateMobileNumber(
        prodmngr?.mobileNumber,
        errors,
        ContactTypes.ProductManager
    )

    const salesreport = stepValues.contactInfo?.salesReporting
    errors = validateFirstname(
        salesreport?.firstname,
        errors,
        ContactTypes.SalesReporting
    )
    errors = validateLastname(
        salesreport?.lastname,
        errors,
        ContactTypes.SalesReporting
    )
    errors = validateContactEmail(
        salesreport?.email,
        errors,
        ContactTypes.SalesReporting
    )
    errors = validateMobileNumber(
        salesreport?.mobileNumber,
        errors,
        ContactTypes.SalesReporting
    )
    return errors
}

export const validateStep5 = async (
    stepValues: Step5ValueModel,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    errors = validateToC(stepValues.terms, currentErrors)

    return errors
}